/**
 * AffDice SuperAdmin messages — the cross-operator affiliate directory and every affiliate popup.
 *
 * Every key starts `aff.` so no other catalogue can collide with it.
 */
import type { Catalogue } from '@affdice/portal-kit';

export const AFFILIATES_MESSAGES = {
  /* Counts shared by several sentences */
  'aff.count.brand.one': { en: '{count} brand', es: '{count} marca', 'pt-BR': '{count} marca' },
  'aff.count.brand.other': { en: '{count} brands', es: '{count} marcas', 'pt-BR': '{count} marcas' },
  'aff.count.operator.one': { en: '{count} operator', es: '{count} operador', 'pt-BR': '{count} operador' },
  'aff.count.operator.other': { en: '{count} operators', es: '{count} operadores', 'pt-BR': '{count} operadores' },
  'aff.count.profile.one': { en: '{count} profile', es: '{count} perfil', 'pt-BR': '{count} perfil' },
  'aff.count.profile.other': { en: '{count} profiles', es: '{count} perfiles', 'pt-BR': '{count} perfis' },

  /* The directory */
  'aff.list.title': { en: 'Affiliates', es: 'Afiliados', 'pt-BR': 'Afiliados' },
  'aff.list.consolidate': { en: 'Consolidate identities', es: 'Consolidar identidades', 'pt-BR': 'Consolidar identidades' },
  'aff.list.consolidationTitle': { en: 'Identity consolidation', es: 'Consolidación de identidades', 'pt-BR': 'Consolidação de identidades' },
  'aff.list.searchPlaceholder': { en: 'Search by name or email', es: 'Buscar por nombre o correo', 'pt-BR': 'Buscar por nome ou e-mail' },
  'aff.list.everyOperator': { en: 'Every operator', es: 'Todos los operadores', 'pt-BR': 'Todos os operadores' },
  'aff.list.panel': { en: 'On the platform', es: 'En la plataforma', 'pt-BR': 'Na plataforma' },
  'aff.list.nobodyYet': { en: 'Nobody yet.', es: 'Todavía nadie.', 'pt-BR': 'Ninguém ainda.' },
  'aff.list.colAffiliate': { en: 'Affiliate', es: 'Afiliado', 'pt-BR': 'Afiliado' },
  'aff.list.colOperators': { en: 'Operators', es: 'Operadores', 'pt-BR': 'Operadores' },
  'aff.list.colBrands': { en: 'Brands', es: 'Marcas', 'pt-BR': 'Marcas' },
  'aff.list.colClicks': { en: 'Clicks', es: 'Clics', 'pt-BR': 'Cliques' },
  'aff.list.colJoined': { en: 'Joined', es: 'Alta', 'pt-BR': 'Entrada' },

  /* Identity consolidation form */
  'aff.consolidate.survivor': { en: 'Surviving identity id', es: 'ID de la identidad que permanece', 'pt-BR': 'ID da identidade que permanece' },
  'aff.consolidate.superseded': { en: 'Superseded identity id', es: 'ID de la identidad reemplazada', 'pt-BR': 'ID da identidade substituída' },
  'aff.consolidate.evidence': { en: 'Evidence', es: 'Evidencia', 'pt-BR': 'Evidência' },
  'aff.consolidate.willWait': {
    en: 'This waits for a second person. Nothing is merged until they approve it from Waiting for approval, signed in on their own account.',
    es: 'Esto espera a una segunda persona. No se fusiona nada hasta que lo apruebe en Esperando aprobación, con su propia cuenta.',
    'pt-BR': 'Isto aguarda uma segunda pessoa. Nada é mesclado até que ela aprove em Aguardando aprovação, na própria conta.',
  },
  'aff.consolidate.waiting': {
    en: 'Waiting for a second person. They approve or reject it from Waiting for approval, signed in on their own account.',
    es: 'En espera de una segunda persona. Esa persona lo aprueba o lo rechaza en Esperando aprobación, con su propia cuenta.',
    'pt-BR': 'Aguardando uma segunda pessoa. Ela aprova ou rejeita em Aguardando aprovação, na própria conta.',
  },
  'aff.approvals.title': { en: 'Waiting for approval', es: 'Esperando aprobación', 'pt-BR': 'Aguardando aprovação' },
  'aff.approvals.intro': {
    en: 'An identity merge does nothing until a different person, signed in on their own account, approves or rejects it.',
    es: 'Una fusión de identidades no hace nada hasta que otra persona, con su propia cuenta, la apruebe o la rechace.',
    'pt-BR': 'Uma mescla de identidades não faz nada até que outra pessoa, na própria conta, a aprove ou a rejeite.',
  },
  'aff.approvals.empty.pending': {
    en: 'Nothing is waiting for a second person.',
    es: 'No hay nada esperando a una segunda persona.',
    'pt-BR': 'Não há nada aguardando uma segunda pessoa.',
  },
  'aff.approvals.empty.approved': {
    en: 'Nothing has been approved.',
    es: 'No se ha aprobado nada.',
    'pt-BR': 'Nada foi aprovado.',
  },
  'aff.approvals.empty.rejected': {
    en: 'Nothing has been rejected.',
    es: 'No se ha rechazado nada.',
    'pt-BR': 'Nada foi rejeitado.',
  },
  'aff.approvals.filter.pending': { en: 'Waiting', es: 'En espera', 'pt-BR': 'Em espera' },
  'aff.approvals.filter.approved': { en: 'Approved', es: 'Aprobado', 'pt-BR': 'Aprovado' },
  'aff.approvals.filter.rejected': { en: 'Rejected', es: 'Rechazado', 'pt-BR': 'Rejeitado' },
  'aff.approvals.col.who': { en: 'Who asked', es: 'Quién lo pidió', 'pt-BR': 'Quem pediu' },
  'aff.approvals.col.what': { en: 'What', es: 'Qué', 'pt-BR': 'O quê' },
  'aff.approvals.col.amount': { en: 'Amount', es: 'Importe', 'pt-BR': 'Valor' },
  'aff.approvals.col.when': { en: 'When', es: 'Cuándo', 'pt-BR': 'Quando' },
  'aff.approvals.what': { en: 'Identity merge', es: 'Fusión de identidades', 'pt-BR': 'Mescla de identidades' },
  'aff.approvals.noAmount': { en: 'No amount', es: 'Sin importe', 'pt-BR': 'Sem valor' },
  'aff.approvals.whenUnknown': { en: 'Time unknown', es: 'Hora desconocida', 'pt-BR': 'Hora desconhecida' },
  'aff.approvals.unknownWho': { en: 'Somebody', es: 'Alguien', 'pt-BR': 'Alguém' },
  'aff.approvals.own': {
    en: 'You asked for this. It is waiting for a second person.',
    es: 'Tú lo pediste. Está esperando a una segunda persona.',
    'pt-BR': 'Você pediu isto. Está aguardando uma segunda pessoa.',
  },
  'aff.approvals.notYours': {
    en: 'A colleague who can edit identities decides it.',
    es: 'Lo decide un colega que puede editar identidades.',
    'pt-BR': 'Um colega que pode editar identidades decide.',
  },
  'aff.approvals.expired': {
    en: 'The deadline has passed. Ask for it again.',
    es: 'El plazo ya pasó. Pídelo de nuevo.',
    'pt-BR': 'O prazo já passou. Peça de novo.',
  },
  'aff.approvals.approve': { en: 'Approve', es: 'Aprobar', 'pt-BR': 'Aprovar' },
  'aff.approvals.reject': { en: 'Reject', es: 'Rechazar', 'pt-BR': 'Rejeitar' },
  'aff.approvals.confirmApprove': { en: 'Approve this merge?', es: '¿Aprobar esta fusión?', 'pt-BR': 'Aprovar esta mescla?' },
  'aff.approvals.confirmReject': { en: 'Reject this merge?', es: '¿Rechazar esta fusión?', 'pt-BR': 'Rejeitar esta mescla?' },
  'aff.approvals.alreadyDecided': {
    en: 'Somebody else already decided this. The list has been refreshed.',
    es: 'Otra persona ya decidió esto. La lista se actualizó.',
    'pt-BR': 'Outra pessoa já decidiu isto. A lista foi atualizada.',
  },
  'aff.approvals.reason': { en: 'Your reason', es: 'Su motivo', 'pt-BR': 'Seu motivo' },
  'aff.approvals.reasonHint': {
    en: 'The only explanation anyone reading this later will have.',
    es: 'Es la única explicación que tendrá quien lea esto más adelante.',
    'pt-BR': 'É a única explicação que quem ler isto depois terá.',
  },
  'aff.approvals.reasonShort': {
    en: 'Say why, in a short sentence. That is what the record keeps.',
    es: 'Di por qué, en una frase corta. Es lo que queda en el registro.',
    'pt-BR': 'Diga por quê, numa frase curta. É o que fica no registro.',
  },
  'aff.approvals.applied': {
    en: 'Approved. The merge is recorded against you.',
    es: 'Aprobado. La fusión queda registrada a tu nombre.',
    'pt-BR': 'Aprovado. A mescla fica registrada em seu nome.',
  },
  'aff.approvals.rejectedDone': {
    en: 'Rejected. Nothing was merged.',
    es: 'Rechazado. No se fusionó nada.',
    'pt-BR': 'Rejeitado. Nada foi mesclado.',
  },
  'aff.approvals.actions': { en: 'Actions', es: 'Acciones', 'pt-BR': 'Ações' },
  'aff.consolidate.governingDeals': { en: 'Governing deals JSON', es: 'JSON de acuerdos vigentes', 'pt-BR': 'JSON de acordos vigentes' },
  'aff.consolidate.governingDealsHint': {
    en: '{"operator_id":"deal_id"} for operators with both profiles',
    es: '{"operator_id":"deal_id"} para los operadores con ambos perfiles',
    'pt-BR': '{"operator_id":"deal_id"} para os operadores com ambos os perfis',
  },
  'aff.consolidate.submit': { en: 'Consolidate identities', es: 'Consolidar identidades', 'pt-BR': 'Consolidar identidades' },
  'aff.consolidate.confirmTitle': { en: 'Merge these identities?', es: '¿Fusionar estas identidades?', 'pt-BR': 'Mesclar estas identidades?' },
  'aff.consolidate.confirmLabel': { en: 'Merge identities', es: 'Fusionar identidades', 'pt-BR': 'Mesclar identidades' },
  'aff.consolidate.mergeIntro': { en: 'This merges', es: 'Esto fusiona a', 'pt-BR': 'Isto mescla' },
  'aff.consolidate.mergeInto': { en: 'into', es: 'en', 'pt-BR': 'em' },
  'aff.consolidate.mergeOutro': {
    en: "— the superseded identity's history moves under the survivor, and this cannot be undone.",
    es: '— el historial de la identidad reemplazada pasa a la que permanece, y esto no se puede deshacer.',
    'pt-BR': '— o histórico da identidade substituída passa para a que permanece, e isto não pode ser desfeito.',
  },

  /* Detail popup */
  'aff.detail.titleFromBrand': { en: 'Affiliate — from {brand}', es: 'Afiliado — desde {brand}', 'pt-BR': 'Afiliado — a partir de {brand}' },
  'aff.detail.operatorHint': {
    en: 'A separate profile under each operator, with its own terms, commission and players.',
    es: 'Un perfil distinto en cada operador, con sus propios términos, comisión y jugadores.',
    'pt-BR': 'Um perfil separado em cada operador, com seus próprios termos, comissão e jogadores.',
  },
  'aff.detail.suspendedEverywhere': { en: 'Suspended everywhere', es: 'Suspendido en todas partes', 'pt-BR': 'Suspenso em todos os lugares' },
  'aff.detail.suspend': { en: 'Suspend…', es: 'Suspender…', 'pt-BR': 'Suspender…' },
  'aff.detail.reactivateEverywhere': { en: 'Reactivate everywhere', es: 'Reactivar en todas partes', 'pt-BR': 'Reativar em todos os lugares' },
  'aff.detail.reactivateAllTitle': { en: 'Reactivate this person everywhere?', es: '¿Reactivar a esta persona en todas partes?', 'pt-BR': 'Reativar esta pessoa em todos os lugares?' },
  'aff.detail.reactivate': { en: 'Reactivate', es: 'Reactivar', 'pt-BR': 'Reativar' },
  'aff.detail.reactivateAllBody': {
    en: 'This person can sign in and their traffic attributes again, on every operator they have a profile under. A brand suspended on its own stays suspended until it is reactivated from its own row.',
    es: 'Esta persona puede iniciar sesión y su tráfico vuelve a atribuirse, en todos los operadores donde tiene un perfil. Una marca suspendida por separado sigue suspendida hasta que se reactive desde su propia fila.',
    'pt-BR': 'Esta pessoa pode entrar e o tráfego dela volta a ser atribuído, em todos os operadores onde tem um perfil. Uma marca suspensa separadamente continua suspensa até ser reativada na própria linha.',
  },
  'aff.detail.suspendOnBrandTitle': { en: 'Suspend on {brand}?', es: '¿Suspender en {brand}?', 'pt-BR': 'Suspender em {brand}?' },
  'aff.detail.reactivateOnBrandTitle': { en: 'Reactivate on {brand}?', es: '¿Reactivar en {brand}?', 'pt-BR': 'Reativar em {brand}?' },
  'aff.detail.suspendOnBrandBody': {
    en: 'Blocks new tracking links for this affiliate on {brand} only. Their standing on every other brand, and their status across this operator, are untouched — links already issued keep tracking and commission already earned is unaffected.',
    es: 'Bloquea los nuevos enlaces de seguimiento de este afiliado solo en {brand}. Su situación en las demás marcas y su estado en este operador no cambian: los enlaces ya emitidos siguen rastreando y la comisión ya ganada no se ve afectada.',
    'pt-BR': 'Bloqueia novos links de rastreamento deste afiliado apenas em {brand}. A situação dele nas demais marcas e o status dele neste operador não mudam — os links já emitidos continuam rastreando e a comissão já ganha não é afetada.',
  },
  'aff.detail.reactivateOnBrandBody': {
    en: 'New tracking links can be created for this affiliate on {brand} again.',
    es: 'Se pueden volver a crear enlaces de seguimiento para este afiliado en {brand}.',
    'pt-BR': 'Novos links de rastreamento podem voltar a ser criados para este afiliado em {brand}.',
  },
  'aff.detail.factLocale': { en: 'Locale', es: 'Idioma', 'pt-BR': 'Idioma' },
  'aff.detail.factJoined': { en: 'Joined', es: 'Alta', 'pt-BR': 'Entrada' },
  'aff.detail.factDormant': { en: 'Dormant', es: 'Inactivo', 'pt-BR': 'Inativo' },
  'aff.detail.factPendingReason': { en: 'Pending reason', es: 'Motivo pendiente', 'pt-BR': 'Motivo pendente' },

  /* Brand enrollments */
  'aff.enroll.title': { en: 'Brand enrollments', es: 'Inscripciones por marca', 'pt-BR': 'Inscrições por marca' },
  'aff.enroll.empty': { en: 'Not enrolled on any brand.', es: 'No está inscrito en ninguna marca.', 'pt-BR': 'Não está inscrito em nenhuma marca.' },
  'aff.enroll.colEnrolled': { en: 'Enrolled', es: 'Inscripción', 'pt-BR': 'Inscrição' },
  'aff.enroll.suspended': { en: 'Suspended', es: 'Suspendido', 'pt-BR': 'Suspenso' },
  'aff.enroll.hintSuspended': {
    en: 'Suspended on {brand}. Reactivate them on this brand.',
    es: 'Suspendido en {brand}. Reactívalo en esta marca.',
    'pt-BR': 'Suspenso em {brand}. Reative-o nesta marca.',
  },
  'aff.enroll.hintActive': {
    en: 'Active on {brand}. Suspend them on this brand only.',
    es: 'Activo en {brand}. Suspéndelo solo en esta marca.',
    'pt-BR': 'Ativo em {brand}. Suspenda-o apenas nesta marca.',
  },

  /* Terms */
  'aff.terms.title': { en: 'Terms in force', es: 'Términos vigentes', 'pt-BR': 'Termos vigentes' },
  'aff.terms.empty': { en: 'No accepted deal on any brand.', es: 'Ningún acuerdo aceptado en ninguna marca.', 'pt-BR': 'Nenhum acordo aceito em nenhuma marca.' },
  'aff.terms.colType': { en: 'Type', es: 'Tipo', 'pt-BR': 'Tipo' },
  'aff.terms.colRevshare': { en: 'Revshare bps', es: 'Revshare bps', 'pt-BR': 'Revshare bps' },
  'aff.terms.colCpa': { en: 'CPA', es: 'CPA', 'pt-BR': 'CPA' },
  'aff.money.title': { en: 'Money', es: 'Dinero', 'pt-BR': 'Dinheiro' },

  /* Tracking links */
  'aff.links.title': { en: 'Tracking links', es: 'Enlaces de seguimiento', 'pt-BR': 'Links de rastreamento' },
  'aff.links.empty': { en: 'No tracking links yet.', es: 'Aún no hay enlaces de seguimiento.', 'pt-BR': 'Ainda não há links de rastreamento.' },
  'aff.links.colCampaign': { en: 'Campaign', es: 'Campaña', 'pt-BR': 'Campanha' },
  'aff.links.colCreated': { en: 'Created', es: 'Creado', 'pt-BR': 'Criado' },
  'aff.links.colClicks': { en: 'Clicks', es: 'Clics', 'pt-BR': 'Cliques' },
  'aff.links.colBotClicks': { en: 'Bot clicks', es: 'Clics de bots', 'pt-BR': 'Cliques de bots' },
  'aff.links.revoked': { en: 'Revoked', es: 'Revocado', 'pt-BR': 'Revogado' },

  /* Dashboard */
  'aff.dash.title': { en: 'Dashboard', es: 'Panel', 'pt-BR': 'Painel' },
  'aff.dash.brandOnly': { en: '{brand} only', es: 'Solo {brand}', 'pt-BR': 'Somente {brand}' },
  'aff.dash.provisional': {
    en: 'Provisional — this period is not locked yet.',
    es: 'Provisional: este período aún no está cerrado.',
    'pt-BR': 'Provisório — este período ainda não foi encerrado.',
  },
  'aff.dash.asOf': { en: 'As of {date}.', es: 'Al {date}.', 'pt-BR': 'Em {date}.' },

  /* Status modal (operator-wide) */
  'aff.status.deactivateTitle': { en: 'Deactivate {name}?', es: '¿Desactivar a {name}?', 'pt-BR': 'Desativar {name}?' },
  'aff.status.reactivateTitle': { en: 'Reactivate {name}?', es: '¿Reactivar a {name}?', 'pt-BR': 'Reativar {name}?' },
  'aff.status.deactivateNote': {
    en: "This is not one brand: it deactivates them across every brand they are enrolled on with this operator. To stop them on one brand only, use that brand's own row under Brand enrollments. Links stop accepting new clicks. Traffic already sent keeps its attribution, and commission already earned is untouched.",
    es: 'No es una sola marca: los desactiva en todas las marcas en las que están inscritos con este operador. Para detenerlos solo en una marca, usa la fila de esa marca en Inscripciones por marca. Los enlaces dejan de aceptar nuevos clics. El tráfico ya enviado conserva su atribución y la comisión ya ganada no se toca.',
    'pt-BR': 'Não é uma única marca: desativa o afiliado em todas as marcas em que está inscrito neste operador. Para interrompê-lo em apenas uma marca, use a linha dessa marca em Inscrições por marca. Os links deixam de aceitar novos cliques. O tráfego já enviado mantém a atribuição e a comissão já ganha não é alterada.',
  },
  'aff.status.reactivateNote': {
    en: 'This reaches every brand they are enrolled on with this operator. Their own links resume accepting clicks.',
    es: 'Esto alcanza todas las marcas en las que están inscritos con este operador. Sus enlaces vuelven a aceptar clics.',
    'pt-BR': 'Isto alcança todas as marcas em que o afiliado está inscrito neste operador. Os links dele voltam a aceitar cliques.',
  },
  'aff.status.blocked.one': {
    en: 'This affiliate has a payable balance on {count} brand — pay it out first, or deactivate anyway below with a reason on record.',
    es: 'Este afiliado tiene un saldo a pagar en {count} marca: págalo primero o desactívalo igualmente abajo, dejando un motivo registrado.',
    'pt-BR': 'Este afiliado tem um saldo a pagar em {count} marca — pague-o primeiro ou desative mesmo assim abaixo, com um motivo registrado.',
  },
  'aff.status.blocked.other': {
    en: 'This affiliate has a payable balance on {count} brands — pay it out first, or deactivate anyway below with a reason on record.',
    es: 'Este afiliado tiene un saldo a pagar en {count} marcas: págalo primero o desactívalo igualmente abajo, dejando un motivo registrado.',
    'pt-BR': 'Este afiliado tem um saldo a pagar em {count} marcas — pague-o primeiro ou desative mesmo assim abaixo, com um motivo registrado.',
  },
  'aff.status.forceGroup': { en: 'Deactivate anyway', es: 'Desactivar igualmente', 'pt-BR': 'Desativar mesmo assim' },
  'aff.status.forceCheck': {
    en: 'Force deactivation despite the payable balance',
    es: 'Forzar la desactivación pese al saldo a pagar',
    'pt-BR': 'Forçar a desativação apesar do saldo a pagar',
  },
  'aff.status.why': {
    en: 'Why (required — this goes on record)',
    es: 'Motivo (obligatorio: queda registrado)',
    'pt-BR': 'Motivo (obrigatório — fica registrado)',
  },
  'aff.status.working': { en: 'Working…', es: 'Procesando…', 'pt-BR': 'Processando…' },
  'aff.status.deactivate': { en: 'Deactivate', es: 'Desactivar', 'pt-BR': 'Desativar' },
  'aff.status.reactivate': { en: 'Reactivate', es: 'Reactivar', 'pt-BR': 'Reativar' },

  /* Suspend modal */
  'aff.suspend.title': { en: 'Suspend {name}', es: 'Suspender a {name}', 'pt-BR': 'Suspender {name}' },
  'aff.suspend.where': { en: 'Where', es: 'Dónde', 'pt-BR': 'Onde' },
  'aff.suspend.whereAria': { en: 'Where to suspend', es: 'Dónde suspender', 'pt-BR': 'Onde suspender' },
  'aff.suspend.scopeBrands': { en: 'Chosen brands', es: 'Marcas elegidas', 'pt-BR': 'Marcas escolhidas' },
  'aff.suspend.scopeBrandsNote': {
    en: 'Only the brands you tick, at whichever operator they belong to.',
    es: 'Solo las marcas que marques, en el operador al que pertenezcan.',
    'pt-BR': 'Apenas as marcas que você marcar, em qualquer operador a que pertençam.',
  },
  'aff.suspend.scopeOperators': { en: 'Chosen operators', es: 'Operadores elegidos', 'pt-BR': 'Operadores escolhidos' },
  'aff.suspend.scopeOperatorsNote': {
    en: 'Deactivates them at each operator you tick, on every brand there.',
    es: 'Los desactiva en cada operador que marques, en todas sus marcas.',
    'pt-BR': 'Desativa o afiliado em cada operador que você marcar, em todas as marcas dele.',
  },
  'aff.suspend.scopeEverywhere': { en: 'Every operator', es: 'Todos los operadores', 'pt-BR': 'Todos os operadores' },
  'aff.suspend.scopeEverywhereNote': {
    en: 'Blocks sign-in and new attribution on every profile they hold.',
    es: 'Bloquea el inicio de sesión y la nueva atribución en todos sus perfiles.',
    'pt-BR': 'Bloqueia o acesso e novas atribuições em todos os perfis que ele tem.',
  },
  'aff.suspend.everywhereNote': {
    en: 'Blocks sign-in and new attribution on every operator this person has a profile under. Links already issued keep working and commission already earned is untouched.',
    es: 'Bloquea el inicio de sesión y la nueva atribución en todos los operadores donde esta persona tiene un perfil. Los enlaces ya emitidos siguen funcionando y la comisión ya ganada no se toca.',
    'pt-BR': 'Bloqueia o acesso e novas atribuições em todos os operadores onde esta pessoa tem um perfil. Os links já emitidos continuam funcionando e a comissão já ganha não é alterada.',
  },
  'aff.suspend.operators': { en: 'Operators', es: 'Operadores', 'pt-BR': 'Operadores' },
  'aff.suspend.already': { en: 'Already {status}', es: 'Ya está {status}', 'pt-BR': 'Já está {status}' },
  'aff.suspend.notEnrolled': { en: 'Not enrolled on any brand', es: 'No está inscrito en ninguna marca', 'pt-BR': 'Não está inscrito em nenhuma marca' },
  'aff.suspend.everyBrandHere': { en: 'Every brand here — {brands}', es: 'Todas las marcas de aquí — {brands}', 'pt-BR': 'Todas as marcas daqui — {brands}' },
  'aff.suspend.brands': { en: 'Brands', es: 'Marcas', 'pt-BR': 'Marcas' },
  'aff.suspend.notEnrolledHere': { en: 'Not enrolled on any brand here.', es: 'No está inscrito en ninguna marca aquí.', 'pt-BR': 'Não está inscrito em nenhuma marca aqui.' },
  'aff.suspend.chipSuspended': { en: 'Suspended', es: 'Suspendido', 'pt-BR': 'Suspenso' },
  'aff.suspend.chipActive': { en: 'Active', es: 'Activo', 'pt-BR': 'Ativo' },
  'aff.suspend.reasonHint': {
    en: 'Goes on record with every change this makes.',
    es: 'Queda registrado con cada cambio que esto hace.',
    'pt-BR': 'Fica registrado em cada alteração feita por isto.',
  },
  'aff.suspend.failureLabel': { en: 'Suspend', es: 'Suspender', 'pt-BR': 'Suspender' },
  'aff.suspend.didNotChangeOne': { en: 'This did not change:', es: 'Esto no cambió:', 'pt-BR': 'Isto não foi alterado:' },
  'aff.suspend.didNotChangeMany': { en: 'These did not change:', es: 'Esto no cambió:', 'pt-BR': 'Isto não foi alterado:' },
  'aff.suspend.forceCheck': {
    en: 'Deactivate despite the payable balance at {operators}',
    es: 'Desactivar pese al saldo a pagar en {operators}',
    'pt-BR': 'Desativar apesar do saldo a pagar em {operators}',
  },
  'aff.suspend.summaryEverywhere': { en: 'Suspends {name} on every operator — {profiles}.', es: 'Suspende a {name} en todos los operadores: {profiles}.', 'pt-BR': 'Suspende {name} em todos os operadores — {profiles}.' },
  'aff.suspend.summaryEverywhereLoading': {
    en: 'Suspends {name} on every operator they hold a profile under.',
    es: 'Suspende a {name} en todos los operadores donde tiene un perfil.',
    'pt-BR': 'Suspende {name} em todos os operadores onde tem um perfil.',
  },
  'aff.suspend.summaryNothing': { en: 'Nothing chosen yet.', es: 'Aún no has elegido nada.', 'pt-BR': 'Nada escolhido ainda.' },
  'aff.suspend.summaryOperators': {
    en: 'Deactivates {name} at {operators}, on every brand there.',
    es: 'Desactiva a {name} en {operators}, en todas sus marcas.',
    'pt-BR': 'Desativa {name} em {operators}, em todas as marcas deles.',
  },
  'aff.suspend.summaryBrands': {
    en: 'Suspends {brands} across {operators}.',
    es: 'Suspende {brands} en {operators}.',
    'pt-BR': 'Suspende {brands} em {operators}.',
  },
  'aff.suspend.summaryBrandsSkipped': {
    en: 'Suspends {brands} across {operators}. {skipped} already suspended, left as they are.',
    es: 'Suspende {brands} en {operators}. {skipped} ya suspendidas, se dejan como están.',
    'pt-BR': 'Suspende {brands} em {operators}. {skipped} já suspensas, mantidas como estão.',
  },
  'aff.suspend.deactivating': { en: 'Deactivating…', es: 'Desactivando…', 'pt-BR': 'Desativando…' },
  'aff.suspend.suspending': { en: 'Suspending…', es: 'Suspendiendo…', 'pt-BR': 'Suspendendo…' },
  'aff.suspend.submitEverywhere': { en: 'Suspend everywhere', es: 'Suspender en todas partes', 'pt-BR': 'Suspender em todos os lugares' },
  'aff.suspend.submitDeactivateAt': { en: 'Deactivate at {operators}', es: 'Desactivar en {operators}', 'pt-BR': 'Desativar em {operators}' },
  'aff.suspend.submitDeactivate': { en: 'Deactivate', es: 'Desactivar', 'pt-BR': 'Desativar' },
  'aff.suspend.submitSuspendOn': { en: 'Suspend on {brands}', es: 'Suspender en {brands}', 'pt-BR': 'Suspender em {brands}' },
  'aff.suspend.submit': { en: 'Suspend', es: 'Suspender', 'pt-BR': 'Suspender' },

  /* Invoices and disputes panels */
  'aff.invoices.title': { en: 'Invoices', es: 'Facturas', 'pt-BR': 'Faturas' },
  'aff.invoices.empty': { en: 'No invoices yet.', es: 'Aún no hay facturas.', 'pt-BR': 'Ainda não há faturas.' },
  'aff.invoices.colNumber': { en: 'Number', es: 'Número', 'pt-BR': 'Número' },
  'aff.invoices.colTotal': { en: 'Total', es: 'Total', 'pt-BR': 'Total' },
  'aff.invoices.colSettled': { en: 'Settled', es: 'Liquidado', 'pt-BR': 'Liquidado' },
  'aff.invoices.colDue': { en: 'Due', es: 'Vencimiento', 'pt-BR': 'Vencimento' },
  'aff.disputes.title': { en: 'Disputes', es: 'Disputas', 'pt-BR': 'Contestações' },
  'aff.disputes.empty': { en: 'No disputes raised.', es: 'No se ha abierto ninguna disputa.', 'pt-BR': 'Nenhuma contestação aberta.' },
  'aff.disputes.colRaised': { en: 'Raised', es: 'Abierta', 'pt-BR': 'Aberta' },
  'aff.disputes.colCategory': { en: 'Category', es: 'Categoría', 'pt-BR': 'Categoria' },
  'aff.disputes.colOutcome': { en: 'Outcome', es: 'Resultado', 'pt-BR': 'Resultado' },
  'aff.disputes.colDisputed': { en: 'Disputed', es: 'En disputa', 'pt-BR': 'Contestado' },
  'aff.disputes.colWhat': { en: 'What happened', es: 'Qué ocurrió', 'pt-BR': 'O que aconteceu' },

  /* Mandates panel */
  'aff.mandates.title': { en: 'Acting for this affiliate', es: 'Actuar en nombre de este afiliado', 'pt-BR': 'Agir em nome deste afiliado' },
  'aff.mandates.empty': { en: 'Nothing has been asked of this affiliate.', es: 'No se le ha pedido nada a este afiliado.', 'pt-BR': 'Nada foi solicitado a este afiliado.' },
  'aff.mandates.colFor': { en: 'For', es: 'Para', 'pt-BR': 'Para' },
  'aff.mandates.colWhy': { en: 'Why we asked', es: 'Por qué lo pedimos', 'pt-BR': 'Por que pedimos' },
  'aff.mandates.colLapses': { en: 'Lapses', es: 'Caduca', 'pt-BR': 'Expira' },
  'aff.mandates.actRaiseDispute': { en: 'Raising a dispute', es: 'Abrir una disputa', 'pt-BR': 'Abrir uma contestação' },
  'aff.mandates.raiseTheDispute': { en: 'Raise the dispute', es: 'Abrir la disputa', 'pt-BR': 'Abrir a contestação' },
  'aff.mandates.usedBy': { en: 'Used by {email}', es: 'Usado por {email}', 'pt-BR': 'Usado por {email}' },
  'aff.mandates.askLabel': {
    en: 'Ask this affiliate for authority to raise a dispute',
    es: 'Pide a este afiliado autorización para abrir una disputa',
    'pt-BR': 'Peça a este afiliado autorização para abrir uma contestação',
  },
  'aff.mandates.askHint': {
    en: 'They decide. Nothing here acts for them until they say yes, and each yes covers one dispute.',
    es: 'Ellos deciden. Nada aquí actúa en su nombre hasta que digan que sí, y cada sí cubre una sola disputa.',
    'pt-BR': 'Ele decide. Nada aqui age em nome dele até que diga sim, e cada sim cobre uma única contestação.',
  },
  'aff.mandates.askPlaceholder': {
    en: 'Ticket number and what they asked for',
    es: 'Número de ticket y lo que pidieron',
    'pt-BR': 'Número do ticket e o que foi pedido',
  },
  'aff.mandates.asking': { en: 'Asking…', es: 'Solicitando…', 'pt-BR': 'Solicitando…' },
  'aff.mandates.ask': { en: 'Ask for a mandate', es: 'Solicitar un mandato', 'pt-BR': 'Solicitar um mandato' },
  'aff.mandates.askNote': {
    en: 'This is the one thing the operator cannot approve on their behalf — a dispute is against the operator, so only the affiliate can authorise it. They can take it back at any time before it is used, and the record names whoever here used it.',
    es: 'Esto es lo único que el operador no puede aprobar en su nombre: una disputa es contra el operador, así que solo el afiliado puede autorizarla. Puede retirarla en cualquier momento antes de que se use, y el registro nombra a quien la usó aquí.',
    'pt-BR': 'Esta é a única coisa que o operador não pode aprovar em nome dele — uma contestação é contra o operador, então somente o afiliado pode autorizá-la. Ele pode retirá-la a qualquer momento antes do uso, e o registro identifica quem a usou aqui.',
  },
  'aff.mandates.raiseTitle': {
    en: "Raise this dispute in the affiliate's name",
    es: 'Abrir esta disputa en nombre del afiliado',
    'pt-BR': 'Abrir esta contestação em nome do afiliado',
  },
  'aff.mandates.raiseNote': {
    en: "The dispute will be the affiliate's; the act will be recorded against you. The mandate is spent either way — it covers one dispute, once.",
    es: 'La disputa será del afiliado; el acto quedará registrado a tu nombre. El mandato se consume en cualquier caso: cubre una sola disputa, una sola vez.',
    'pt-BR': 'A contestação será do afiliado; o ato será registrado em seu nome. O mandato é consumido de qualquer forma — cobre uma única contestação, uma única vez.',
  },
  'aff.mandates.category': { en: 'Category', es: 'Categoría', 'pt-BR': 'Categoria' },
  'aff.mandates.catCommissionFigure': { en: 'Commission figure', es: 'Importe de comisión', 'pt-BR': 'Valor da comissão' },
  'aff.mandates.catAttribution': { en: 'Attribution', es: 'Atribución', 'pt-BR': 'Atribuição' },
  'aff.mandates.catDeduction': { en: 'Deduction', es: 'Deducción', 'pt-BR': 'Dedução' },
  'aff.mandates.catClawback': { en: 'Clawback', es: 'Recuperación', 'pt-BR': 'Estorno' },
  'aff.mandates.catHold': { en: 'Hold', es: 'Retención', 'pt-BR': 'Retenção' },
  'aff.mandates.catSettlement': { en: 'Settlement', es: 'Liquidación', 'pt-BR': 'Liquidação' },
  'aff.mandates.catStatusChange': { en: 'Status change', es: 'Cambio de estado', 'pt-BR': 'Mudança de status' },
  'aff.mandates.whatHappened': { en: 'What happened', es: 'Qué ocurrió', 'pt-BR': 'O que aconteceu' },
  'aff.mandates.whatHappenedHint': {
    en: "In the affiliate's words, as they gave them — not a summary of them.",
    es: 'Con las palabras del afiliado, tal como las dio; no un resumen.',
    'pt-BR': 'Nas palavras do afiliado, como ele as deu — não um resumo.',
  },
  'aff.mandates.raising': { en: 'Raising…', es: 'Abriendo…', 'pt-BR': 'Abrindo…' },
  'aff.mandates.raiseIt': { en: 'Raise it', es: 'Abrirla', 'pt-BR': 'Abrir' },
} as const satisfies Catalogue;
